import { asyncDerived, asyncWritable, type Readable } from '@square/svelte-store';
import { shopify } from '../shopify';
import { cartId, deleteCartId, updateCartId } from './cart-id';
import { user } from '$lib/stores/user';
import { buildPath, getFullURL } from '$lib/utils/paths';
import { derived, writable } from 'svelte/store';
import { lang } from '$lib/lang';
import type { LanguageCode } from '$gql/storefront/schemas';
import type { AddToCartLocations, ElevarProduct } from '$lib/analytics';
import { page } from '$app/stores';
import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';
import { DEFAULT_VENDOR, PRODUCT_CATEGORIES } from '$lib/constants';
import { region } from '$lib/region';

// THE cart
export const cart = asyncWritable(
  [shopify, cartId, lang, page],
  async ([$shopify, $cartId, $lang]) => {
    if (!$cartId) return null;

    const data = await $shopify.getCart({
      id: $cartId,
      lang: $lang.toUpperCase() as LanguageCode
    });

    // if the cart doesn't exist, delete it from storage
    if (!data.cart?.id) {
      deleteCartId();
      return null;
    }

    // update ID in storage to make sure it's up to date
    updateCartId(data.cart.id);

    // return cart data
    return data.cart;
  },
  async (newCart, deps) => {
    const [, $cartId] = deps ?? [];

    if (newCart?.id && newCart.id !== $cartId) {
      updateCartId(newCart.id);
    }
  },
  {
    reloadable: true,
    trackState: true
  }
);

export const checkoutUrl = asyncDerived(
  [cart, user, buildPath, lang],
  async ([$cart, $user, $buildPath, $lang]) => {
    const checkoutUrl = $cart?.checkoutUrl ? new URL($cart.checkoutUrl) : undefined;

    if (!checkoutUrl) return undefined;

    // append language to the checkout
    checkoutUrl.searchParams.append('locale', $lang);

    if (!$user) return checkoutUrl;

    const res = await fetch(
      $buildPath({
        path: `/get-checkout-url?checkout_url=${encodeURIComponent(checkoutUrl?.toString())}`
      })
    );

    if (!res.ok) {
      return checkoutUrl.toString();
    }

    return await res.text();
  },
  {
    trackState: true
  }
);

// a simple writable store to save which was the last added item
export const lastAddedItemId = writable<string | null>(null);

export const lastAddedItemLocation = (() => {
  const store = writable<AddToCartLocations | undefined>(undefined);
  return { ...store, reset: () => store.set(undefined) };
})();

export const getProductCategory = (productName: string, productType?: string): string | null => {
  let name = productName.toLowerCase();

  if (productType && productType) {
    name = productType.toLowerCase();
  }

  for (const category in PRODUCT_CATEGORIES) {
    for (const subCategory of PRODUCT_CATEGORIES[category]) {
      const regex = new RegExp(`\\b${subCategory.toLowerCase()}\\b`);
      if (regex.test(name)) {
        return category;
      }
    }
  }

  return null;
};

// get cart products for elevar tracking
export const cartProducts: Readable<
  (productType?: 'elevarImpressions' | 'elevarProducts') => unknown
> = derived([cart, region, lang], ([$cart, $region, $lang]) => {
  return (productType?: 'elevarImpressions' | 'elevarProducts') => {
    if (!cart) return;

    const cartItems = $cart?.lines.nodes.filter((line) => line.quantity > 0) ?? [];
    const products = cartItems.map((line, index) => {
      const variantId = trimVariantId(line.merchandise.id);
      const collectionName =
        getProductCategory(line.merchandise.product.title, line.merchandise.product.productType) ||
        '';

      return {
        id: line.merchandise.sku,
        name: line.merchandise.product.title,
        brand: line.merchandise.product.vendor || DEFAULT_VENDOR,
        category: line.merchandise.product.productType,
        variant: line.merchandise.product.title,
        price: `${line.cost.totalAmount.amount}`,
        quantity: `${line.quantity}`,
        list: `/collections/${collectionName}`,
        product_id: trimId(line.merchandise.product.id),
        variant_id: variantId,
        compare_at_price: `${line.merchandise.compareAtPrice?.amount}` || '0.0',
        image: line.merchandise.image?.url,
        url: getFullURL({
          path: `/products/${line.merchandise.product.handle}`,
          region: $region,
          lang: $lang,
          searchQuery: `variant=${variantId}` || ''
        }),
        position: index + 1
      };
    });

    const mapToCommonFields = (product: ElevarProduct) => ({
      id: product.id,
      name: product.name,
      brand: product.brand,
      category: product.category,
      variant: product.variant,
      price: product.price,
      product_id: product.product_id,
      variant_id: product.variant_id,
      compare_at_price: product.compare_at_price,
      image: product.image || '',
      url: product.url || '',
      quantity: product.quantity,
      position: product.position
    });

    if (productType === 'elevarProducts') {
      return products.map((product) => ({
        ...mapToCommonFields(product),
        list: product.list
      }));
    }

    if (productType === 'elevarImpressions') {
      return products.map((product) => ({
        ...mapToCommonFields(product)
      }));
    }

    return products || [];
  };
});

/* GraphQL */ `
  fragment CompleteCart on Cart {
    id
    checkoutUrl
    totalQuantity
    discountCodes {
      applicable
      code
    }
    discountAllocations {
      __typename
      discountedAmount {
        ...Money
      }
      ... on CartCodeDiscountAllocation {
        code
      }
      ... on CartCustomDiscountAllocation {
        title
      }
    }
    cost {
      totalAmount {
        ...Money
      }
      totalAmountEstimated
      subtotalAmount {
        ...Money
      }
      subtotalAmountEstimated
      totalTaxAmount {
        ...Money
      }
      totalTaxAmountEstimated
      totalDutyAmount {
        ...Money
      }
      totalDutyAmountEstimated
    }
    lines(first: 250, reverse: true) {
      nodes {
        attributes {
          key
          value
        }
        discountAllocations {
          __typename
          discountedAmount {
            ...Money
          }
          ... on CartCustomDiscountAllocation {
            title
          }
        }
        id
        quantity
        cost {
          totalAmount {
            ...Money
          }
          amountPerQuantity {
            ...Money
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            sku
            compareAtPrice {
              ...Money
            }
            price {
              ...Money
            }
            image {
              url(transform: { preferredContentType: WEBP, maxWidth: 192 })
              altText
            }
            selectedOptions {
              name
              value
            }
            product {
              availableForSale
              id
              handle
              title
              tags
              productType
              vendor
              metafields(
                identifiers: [
                  { namespace: "custom", key: "product_card_pattern_url" }
                  { namespace: "custom", key: "variantcolor" }
                  { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
                  { namespace: "wishlist", key: "wishlist_counter" }
                  { namespace: "custom", key: "pre_order_day" }
                ]
              ) {
                key
                value
              }
              options {
                name
                values
              }
              images(first: 100) {
                nodes {
                  url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
                  altText
                  width
                  height
                  id
                  __typename
                }
              }
              variants(first: 100) {
                nodes {
                  id
                  title
                  availableForSale
                  currentlyNotInStock
                  quantityAvailable
                  sku
                  price {
                    ...Money
                  }
                  compareAtPrice {
                    ...Money
                  }
                  sellingPlanAllocations(first: 10) {
                    edges {
                      node {
                        sellingPlan {
                          id
                          name
                        }
                        priceAdjustments {
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                  }
                }
              }
              sellingPlanGroups(first: 1) {
                edges {
                  node {
                    name
                    sellingPlans(first: 10) {
                      edges {
                        node {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            id
            name
          }
          priceAdjustments {
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            perDeliveryPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }

  query getCart($id: ID!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    cart(id: $id) {
      ...CompleteCart
    }
  }
`;
